import { createRendererStore, type LayerRender, makeOffscreen, paintIndicators } from "#canvas/renderers.ts";
import type { Layer, PixelOp } from "#canvas/types.ts";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";

interface LayerRenderers {
  renderers: Map<string, LayerRender>;
  indicatorRender: LayerRender | null;
  previewRender: LayerRender | null;
}

/**
 * Owns per-layer offscreen canvases plus shared indicator/preview surfaces.
 * Renderers live outside React's render cycle (mutable Map observed via
 * useSyncExternalStore), so textures stay stable across commits — pixi sees
 * dirty-source updates, not texture churn between strokes.
 *
 * Unmount cleanup is deferred one tick so StrictMode's simulated unmount
 * doesn't destroy textures the second mount still uses.
 *
 * @param layers Controlled layer list. Identity drives store sync + repaint.
 * @param cols Canvas width in cells. Triggers full texture rebuild on change.
 * @param rows Canvas height in cells. Triggers full texture rebuild on change.
 * @param indicators Transient overlay pixels (hover dots, cursor preview, etc).
 *   `undefined` clears the indicator surface.
 * @returns `{ renderers, indicatorRender, previewRender }`. `renderers` is a
 *   stable Map keyed by layer id; `indicatorRender` and `previewRender` are
 *   shared offscreens — `null` only if the browser refuses a 2D context.
 */
export function useLayerRenderers(
  layers: Layer[],
  cols: number,
  rows: number,
  indicators: PixelOp[] | undefined,
): LayerRenderers {
  const store = useMemo(() => createRendererStore(), []);
  const renderers = useSyncExternalStore(store.subscribe, store.getSnapshot);

  useEffect(() => {
    store.sync(layers, cols, rows);
  }, [store, layers, cols, rows]);

  useEffect(() => {
    store.repaint(layers);
  }, [store, layers]);

  const indicatorRender: LayerRender | null = useMemo(
    () => makeOffscreen(cols, rows),
    [cols, rows],
  );
  const prevIndicatorRef = useRef<LayerRender | null>(indicatorRender);
  useEffect(() => {
    const stale = prevIndicatorRef.current;
    if (stale && stale !== indicatorRender) stale.texture.destroy(true);
    prevIndicatorRef.current = indicatorRender;
  }, [indicatorRender]);
  useEffect(() => {
    if (indicatorRender) paintIndicators(indicatorRender, indicators ?? []);
  }, [indicators, indicatorRender]);

  const previewRender: LayerRender | null = useMemo(
    () => makeOffscreen(cols, rows),
    [cols, rows],
  );
  const prevPreviewRef = useRef<LayerRender | null>(previewRender);
  useEffect(() => {
    const stale = prevPreviewRef.current;
    if (stale && stale !== previewRender) stale.texture.destroy(true);
    prevPreviewRef.current = previewRender;
  }, [previewRender]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      const ind = prevIndicatorRef.current;
      const prev = prevPreviewRef.current;
      setTimeout(() => {
        if (aliveRef.current) return;
        store.destroyAll();
        if (ind && !ind.texture.destroyed) ind.texture.destroy(true);
        if (prev && !prev.texture.destroyed) prev.texture.destroy(true);
      }, 0);
    };
  }, [store]);

  return { renderers, indicatorRender, previewRender };
}
